import { useMemo } from 'react';

import { MetricBoxConfig, MetricsBoxes } from '@/components/common/DataTable';
import type { PaginationActions, PaginationState } from '@/hooks/usePagination';
import {
  MatterListMetadata,
  MatterStatus,
} from '@/services/types/matter-types';

import {
  ACTIVE_MATTER_STATUSES,
  DEMAND_STATUSES,
  TREATMENT_STATUSES,
} from '../config/matterStatusConfig';

interface MatterMetricsBoxesProps {
  paginationState: PaginationState;
  paginationActions: PaginationActions;
  metadata?: MatterListMetadata;
  isManagingParalegal: boolean;
}

/**
 * Matter metrics with conditional personalization
 * - Managing paralegals: Personal metrics (matters they manage)
 * - Everyone else: Global metrics (all matters)
 * Acts as filters - clicking sets the status filter in URL
 * Positioned top-right of matters page, aligned with title
 */
export const MatterMetricsBoxes = ({
  paginationState,
  paginationActions,
  metadata,
  isManagingParalegal,
}: MatterMetricsBoxesProps) => {
  const boxes: MetricBoxConfig[] = useMemo(() => {
    // Use personal metrics for managing paralegals, global for everyone else
    const counts = isManagingParalegal
      ? metadata?.personal_status_counts || []
      : metadata?.status_counts || [];

    const getCountForCategory = (category: string): number =>
      counts.find((s) => s.status === category)?.count || 0;

    // For managing paralegals, use personal categories and labels
    // For everyone else, use global categories and labels
    if (isManagingParalegal) {
      return [
        {
          id: 'active-matters',
          label: 'Active Matters',
          count: getCountForCategory('my_total_active'),
          tooltip: 'All active matters you are managing',
          filters: {
            status: ACTIVE_MATTER_STATUSES,
          },
        },
        {
          id: 'initial-review',
          label: 'Initial Review',
          count: getCountForCategory('my_initial_review'),
          tooltip: 'Your matters with Initial Review status',
          filters: {
            status: [MatterStatus.INITIAL_REVIEW],
          },
        },
        {
          id: 'case-setup',
          label: 'Case Setup',
          count: getCountForCategory('my_case_setup'),
          tooltip: 'Your matters with Case Setup status',
          filters: {
            status: [MatterStatus.CASE_SETUP],
          },
        },
        {
          id: 'treatment',
          label: 'Treatment',
          count: getCountForCategory('my_treatment'),
          tooltip: 'Your matters with Treatment or Treatment Complete status',
          filters: {
            status: TREATMENT_STATUSES,
          },
        },
        {
          id: 'demand',
          label: 'Demand',
          count: getCountForCategory('my_demand'),
          tooltip:
            'Your matters with Demand Letter, Ready for Demand, or Demand Preparation status',
          filters: {
            status: DEMAND_STATUSES,
          },
        },
      ];
    }

    // Global metrics for non-managing paralegals
    // Note: "Active Matters" count comes from backend's "Total Active" category
    // which sums all matters with active statuses
    return [
      {
        id: 'active-matters',
        label: 'Active Matters',
        count: getCountForCategory('Total Active'),
        tooltip: 'All matters with active status',
        filters: {
          status: ACTIVE_MATTER_STATUSES,
        },
      },
      {
        id: 'initial-review',
        label: 'Initial Review',
        count: getCountForCategory('Initial Review'),
        tooltip: 'Matters with Initial Review status',
        filters: {
          status: [MatterStatus.INITIAL_REVIEW],
        },
      },
      {
        id: 'case-setup',
        label: 'Case Setup',
        count: getCountForCategory('Case Setup'),
        tooltip: 'Matters with Case Setup status',
        filters: {
          status: [MatterStatus.CASE_SETUP],
        },
      },
      {
        id: 'treatment',
        label: 'Treatment',
        count: getCountForCategory('Treatment'),
        tooltip: 'Matters with Treatment or Treatment Complete status',
        filters: {
          status: TREATMENT_STATUSES,
        },
      },
      {
        id: 'demand',
        label: 'Demand',
        count: getCountForCategory('Demand'),
        tooltip:
          'Matters with Demand Letter, Ready for Demand, or Demand Preparation status',
        filters: {
          status: DEMAND_STATUSES,
        },
      },
    ];
  }, [metadata, isManagingParalegal]);

  return (
    <MetricsBoxes
      boxes={boxes}
      paginationState={paginationState}
      paginationActions={paginationActions}
      metricFilterKeys={['status']}
      size="compact"
    />
  );
};
